// src/hooks/useSessionHeartbeat.ts
// Writes users/{uid}.lastActive every 90s and manages a session doc.

import { useEffect, useRef } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { startSession, heartbeatSession, endSession } from '../lib/services/sessionService';

const HEARTBEAT_INTERVAL_MS = 90_000; // 90 seconds

export function useSessionHeartbeat(uid: string | null) {
  const sessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!uid) return;

    let interval: ReturnType<typeof setInterval>;

    const updateLastActive = () => {
      updateDoc(doc(db, 'users', uid), { lastActive: serverTimestamp() }).catch(() => {});
      if (sessionIdRef.current) {
        heartbeatSession(sessionIdRef.current).catch(() => {});
      }
    };

    // Start session + immediate heartbeat
    startSession(uid)
      .then((id) => {
        sessionIdRef.current = id;
        updateLastActive();
      })
      .catch(() => {
        // Still do lastActive even if session creation fails
        updateLastActive();
      });

    interval = setInterval(updateLastActive, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      // End session on unmount
      if (sessionIdRef.current) {
        endSession(sessionIdRef.current).catch(() => {});
        sessionIdRef.current = null;
      }
    };
  }, [uid]);
}
